import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import { useNavigate, useParams } from 'react-router-dom';
import EquipmentForm from './components/EquipmentForm';
import OtherForm from './components/OtherForm';
import ConductorForm from './components/ConductorForm';
import ComponentList from './components/ComponentList';
import EquipmentCallout from './components/EquipmentCallout';
import { ExistingPole, Pole, Transformer, Anchor, Switch, TerminatingCabinet, SecondaryPedestal, GeneralNote, TreeTrimming, Meter } from './components/EquipmentIcons';
import { loadProject, saveProject, loadSharedProject } from "./services/cloudFirstSync";
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import './GridMapProApp.css';

// Set Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

const MAP_STYLES = {
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  streets: 'mapbox://styles/mapbox/streets-v12'
};

const equipmentIcons = {
  'existing-pole': ExistingPole,
  'pole': Pole,
  'transformer': Transformer,
  'anchor': Anchor,
  'switch': Switch,
  'terminating-cabinet': TerminatingCabinet,
  'secondary-pedestal': SecondaryPedestal,
  'general-note': GeneralNote,
  'tree-trimming': TreeTrimming,
  'meter': Meter
};

const equipmentOptions = [
  { value: 'existing-pole', label: 'Existing Pole' },
  { value: 'pole', label: 'Pole' },
  { value: 'transformer', label: 'Transformer' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'switch', label: 'Switch' },
  { value: 'terminating-cabinet', label: 'Terminating Cabinet' },
  { value: 'secondary-pedestal', label: 'Secondary Pedestal' },
  { value: 'meter', label: 'Meter' }
];

const otherOptions = [
  { value: 'general-note', label: 'General Note' },
  { value: 'tree-trimming', label: 'Tree Trimming' }
];

const conductorStyles = {
  'overhead-conductor': {
    'line-dasharray': [2, 2],
    'line-color': '#000000'
  },
  'underground-conductor': {
    'line-dasharray': [2, 2],
    'line-color': '#000000'
  },
  'overhead-service': {
    'line-dasharray': [1, 1],
    'line-color': '#000000'
  },
  'underground-service': {
    'line-dasharray': [1, 1],
    'line-color': '#000000'
  }
};

const GridMapProApp = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapStyle, setMapStyle] = useState('streets');
  const [showEquipmentSelect, setShowEquipmentSelect] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [clickedLngLat, setClickedLngLat] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [isDrawingConductor, setIsDrawingConductor] = useState(false);
  const [conductorPoints, setConductorPoints] = useState([]);
  const [showConductorForm, setShowConductorForm] = useState(false);
  const [drawingMessage, setDrawingMessage] = useState('');
  const tempLineRef = useRef(null);
  const firstPointMarkerRef = useRef(null);
  const isDrawingConductorRef = useRef(false);
  const conductorPointsRef = useRef([]);
  const [isComponentListOpen, setIsComponentListOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [editingConductor, setEditingConductor] = useState(null);
  const [visibleCallouts, setVisibleCallouts] = useState(new Set());
  // New state for "Other" dropdown
  const [showOtherSelect, setShowOtherSelect] = useState(false);
  const [selectedOtherType, setSelectedOtherType] = useState(null);

  // Initialize map
  useEffect(() => {
    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: MAP_STYLES[mapStyle],
        center: [-122.4194, 37.7749],
        zoom: 13
      });

      // Add navigation control
      map.current.addControl(new mapboxgl.NavigationControl());

      // Add geolocate control
      const geolocate = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true,
        showAccuracyCircle: true
      });

      map.current.addControl(geolocate, 'top-right');

      // Function to add conductor layers
      const addConductorLayers = () => {
        // Add source and layer for conductors if they don't exist
        if (!map.current.getSource('conductors')) {
          map.current.addSource('conductors', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            }
          });
        }

        if (!map.current.getSource('temp-line')) {
          map.current.addSource('temp-line', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: []
              }
            }
          });
        }

        // Add a general conductor layer first (without type filtering)
        if (!map.current.getLayer('conductors-general')) {
          map.current.addLayer({
            id: 'conductors-general',
            type: 'line',
            source: 'conductors',
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
              visibility: 'visible'
            },
            paint: {
              'line-width': 3,
              'line-color': '#000000',
              'line-dasharray': [2, 2]
            }
          });
        }

        // Add layers for each conductor type
        Object.entries(conductorStyles).forEach(([type, style]) => {
          const layerId = `conductors-${type}`;
          if (!map.current.getLayer(layerId)) {
            map.current.addLayer({
              id: layerId,
              type: 'line',
              source: 'conductors',
              layout: {
                'line-cap': 'round',
                'line-join': 'round',
                visibility: 'visible'
              },
              paint: {
                'line-width': 3,
                ...style
              },
              filter: ['==', 'type', type]
            });
          }
        });

        // Add temporary line layer
        if (!map.current.getLayer('temp-line')) {
          map.current.addLayer({
            id: 'temp-line',
            type: 'line',
            source: 'temp-line',
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
              visibility: 'visible'
            },
            paint: {
              'line-width': 3,
              'line-color': '#000000',
              'line-dasharray': [2, 2]
            }
          });
        }
      };

      // Add layers when map loads
      map.current.on('load', () => {
        geolocate.trigger();
        addConductorLayers();
      });

      // Re-add layers when style changes
      map.current.on('style.load', () => {
        addConductorLayers();
      });

      // Handle map clicks
      map.current.on('click', (e) => {
        if (isReadOnly) {
          // In read-only mode, only allow viewing callouts
          return;
        }
        
        const handleMapClick = (e) => {
          // Hide all callouts when clicking on the map
          hideAllCallouts();
          
          if (isDrawingConductorRef.current) {
            const newPoint = [e.lngLat.lng, e.lngLat.lat];
            const newPoints = [...conductorPointsRef.current, newPoint];

            if (newPoints.length === 1) {
              // First click - add marker and update preview line
              setConductorPoints(newPoints);
              conductorPointsRef.current = newPoints;
              const el = document.createElement('div');
              el.className = 'conductor-point-marker';
              firstPointMarkerRef.current = new mapboxgl.Marker(el)
                .setLngLat(newPoint)
                .addTo(map.current);
              setDrawingMessage('Click to set the second point of the conductor');
            } else if (newPoints.length === 2) {
              // Second click - finalize the conductor
              setConductorPoints(newPoints);
              conductorPointsRef.current = newPoints;
              setShowConductorForm(true);
              setIsDrawingConductor(false);
              setDrawingMessage('');
            }
          } else {
            setClickedLngLat([e.lngLat.lng, e.lngLat.lat]);
            setShowEquipmentSelect(true);
            setShowOtherSelect(true);
          }
        };
        
        handleMapClick(e);
      });
    }
  }, [isReadOnly]);

  // Handle drawing state changes
  useEffect(() => {
    isDrawingConductorRef.current = isDrawingConductor;
    
    if (isDrawingConductor) {
      setDrawingMessage('Click to set the first point of the conductor');
      setConductorPoints([]); // Clear points when starting
      conductorPointsRef.current = []; // Clear ref as well
      // Clear any existing temporary line
      if (map.current && map.current.getSource('temp-line')) {
        map.current.getSource('temp-line').setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        });
      }
      // Clear any existing marker
      if (firstPointMarkerRef.current) {
        firstPointMarkerRef.current.remove();
        firstPointMarkerRef.current = null;
      }
    } else {
      if (!showConductorForm) { // Only clear if we're not showing the form
        setDrawingMessage('');
        setConductorPoints([]); // Clear points when canceling
        conductorPointsRef.current = []; // Clear ref as well
        // Clear temporary line and marker
        if (map.current && map.current.getSource('temp-line')) {
          map.current.getSource('temp-line').setData({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: []
            }
          });
        }
        if (firstPointMarkerRef.current) {
          firstPointMarkerRef.current.remove();
          firstPointMarkerRef.current = null;
        }
      }
    }
  }, [isDrawingConductor, showConductorForm]);

  // Update message based on conductor points
  useEffect(() => {
    if (isDrawingConductor) {
      if (conductorPoints.length === 0) {
        setDrawingMessage('Click to set the first point of the conductor');
      } else if (conductorPoints.length === 1) {
        setDrawingMessage('Click to set the second point of the conductor');
      }
    }
  }, [conductorPoints, isDrawingConductor]);

  // Handle style changes
  useEffect(() => {
    if (map.current) {
      map.current.setStyle(MAP_STYLES[mapStyle]);
    }
  }, [mapStyle]);

  // Restore data after style changes
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    // Small delay to ensure layers are added
    const timer = setTimeout(() => {
      // Restore conductor data
      if (conductors.length > 0 && map.current.getSource('conductors')) {
        const features = conductors.map(conductor => ({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: conductor.coordinates
          },
          properties: {
            type: conductor.type,
            wireSize: conductor.wireSize,
            length: conductor.length
          }
        }));
        
        map.current.getSource('conductors').setData({
          type: 'FeatureCollection',
          features: features
        });
        
        // Re-add labels for conductors
        conductors.forEach(conductor => {
          const coordinates = conductor.coordinates;
          const start = coordinates[0];
          const end = coordinates[1];
          
          // Calculate midpoint for label placement
          const midpoint = [
            (start[0] + end[0]) / 2,
            (start[1] + end[1]) / 2
          ];

          // Create label container
          const labelContainer = document.createElement('div');
          labelContainer.className = 'conductor-label-container';

          // Create label element
          const label = document.createElement('div');
          label.className = 'conductor-label';
          const length = parseFloat(conductor.length);
          label.innerHTML = `${conductor.wireSize}<br>${isNaN(length) ? '0.0' : length.toFixed(1)} ft`;

          // Add label to container
          labelContainer.appendChild(label);

          // Add marker with label
          new mapboxgl.Marker({
            element: labelContainer,
            anchor: 'center',
            offset: [0, -2]
          })
            .setLngLat(midpoint)
            .addTo(map.current);

          // Calculate angle for label rotation
          const angle = Math.atan2(end[1] - start[1], end[0] - start[0]) * 180 / Math.PI;
          let textAngle = angle;
          if (angle > 90 || angle < -90) {
            textAngle += 180;
          }
          label.style.transform = `rotate(${textAngle}deg)`;
        });
      }
      
      // Restore equipment markers
      if (equipment.length > 0) {
        equipment.forEach((item, index) => {
          const el = document.createElement('div');
          el.className = 'equipment-marker';
          el.style.cursor = 'pointer';
          const IconComponent = equipmentIcons[item.type];
          
          // Create a React root and render the icon
          const root = document.createElement('div');
          el.appendChild(root);
          ReactDOM.createRoot(root).render(<IconComponent />);

          // Add click handler for callout
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            
            // Check if we're currently drawing a conductor
            if (isDrawingConductorRef.current) {
              // Use the equipment position as a conductor endpoint
              const equipmentPoint = [item.lngLat[0], item.lngLat[1]];
              const newPoints = [...conductorPointsRef.current, equipmentPoint];

              if (newPoints.length === 1) {
                // First click - add marker and update preview line
                setConductorPoints(newPoints);
                conductorPointsRef.current = newPoints;
                const el = document.createElement('div');
                el.className = 'conductor-point-marker';
                firstPointMarkerRef.current = new mapboxgl.Marker(el)
                  .setLngLat(equipmentPoint)
                  .addTo(map.current);
                setDrawingMessage('Click to set the second point of the conductor');
              } else if (newPoints.length === 2) {
                // Second click - finalize the conductor
                setConductorPoints(newPoints);
                conductorPointsRef.current = newPoints;
                setShowConductorForm(true);
                setIsDrawingConductor(false);
                setDrawingMessage('');
              }
            } else {
              // Normal behavior - show equipment callout
              showEquipmentCallout(index);
            }
          });

          new mapboxgl.Marker(el)
            .setLngLat(item.lngLat)
            .addTo(map.current);
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [mapStyle, conductors, equipment]);

  // Add CSS for conductor labels
  useEffect(() => {
    // Add style element if it doesn't exist
    if (!document.getElementById('conductor-label-styles')) {
      const style = document.createElement('style');
      style.id = 'conductor-label-styles';
      style.innerHTML = `
        .conductor-label-container {
          background: none !important;
          border: none !important;
        }
        .conductor-label {
          background: none;
          padding: 2px 4px;
          font-size: 12px;
          white-space: nowrap;
          border: none;
          color: #000;
          text-align: center;
          pointer-events: none;
          transform-origin: center;
          text-shadow: 
            -1px -1px 0 #fff,
            1px -1px 0 #fff,
            -1px 1px 0 #fff,
            1px 1px 0 #fff;
          line-height: 1.2;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Add equipment markers to map
  useEffect(() => {
    if (!map.current) return;

    // Clear existing markers
    const markers = document.getElementsByClassName('equipment-marker');
    while (markers[0]) {
      markers[0].remove();
    }

    // Add new markers
    equipment.forEach((item, index) => {
      const el = document.createElement('div');
      el.className = 'equipment-marker';
      el.style.cursor = 'pointer';
      const IconComponent = equipmentIcons[item.type];
      
      // Create a React root and render the icon
      const root = document.createElement('div');
      el.appendChild(root);
      ReactDOM.createRoot(root).render(<IconComponent />);

      // Add click handler for callout
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        
        // Check if we're currently drawing a conductor
        if (isDrawingConductorRef.current) {
          // Use the equipment position as a conductor endpoint
          const equipmentPoint = [item.lngLat[0], item.lngLat[1]];
          const newPoints = [...conductorPointsRef.current, equipmentPoint];

          if (newPoints.length === 1) {
            // First click - add marker and update preview line
            setConductorPoints(newPoints);
            conductorPointsRef.current = newPoints;
            const el = document.createElement('div');
            el.className = 'conductor-point-marker';
            firstPointMarkerRef.current = new mapboxgl.Marker(el)
              .setLngLat(equipmentPoint)
              .addTo(map.current);
            setDrawingMessage('Click to set the second point of the conductor');
          } else if (newPoints.length === 2) {
            // Second click - finalize the conductor
            setConductorPoints(newPoints);
            conductorPointsRef.current = newPoints;
            setShowConductorForm(true);
            setIsDrawingConductor(false);
            setDrawingMessage('');
          }
        } else {
          // Normal behavior - show equipment callout
          showEquipmentCallout(index);
        }
      });

      new mapboxgl.Marker(el)
        .setLngLat(item.lngLat)
        .addTo(map.current);
    });
  }, [equipment]);

  // Update conductor source when conductors change (for new conductors)
  useEffect(() => {
    console.log('=== CONDUCTOR EFFECT DEBUG ===');
    console.log('Conductor effect triggered. Map exists:', !!map.current, 'Conductors count:', conductors.length);
    console.log('Conductors state:', conductors);
    console.log('Conductors state type:', typeof conductors);
    console.log('Conductors is array:', Array.isArray(conductors));
    
    if (map.current && conductors.length > 0) {
      console.log('Conductor effect processing', conductors.length, 'conductors');
      console.log('Conductor data:', conductors.map(c => ({ type: c.type, coordinates: c.coordinates })));
      
      // Ensure conductor source exists
      if (!map.current.getSource('conductors')) {
        console.log('Creating conductor source in effect...');
        map.current.addSource('conductors', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
      } else {
        console.log('Conductor source already exists');
      }
      
      // Ensure conductor layers exist
      if (!map.current.getLayer('conductors-general')) {
        console.log('Creating conductor layers in effect...');
        map.current.addLayer({
          id: 'conductors-general',
          type: 'line',
          source: 'conductors',
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
            visibility: 'visible'
          },
          paint: {
            'line-width': 3,
            'line-color': '#000000',
            'line-dasharray': [2, 2]
          }
        });
      } else {
        console.log('Conductor general layer already exists');
      }

      // Add layers for each conductor type if they don't exist
      Object.entries(conductorStyles).forEach(([type, style]) => {
        const layerId = `conductors-${type}`;
        if (!map.current.getLayer(layerId)) {
          console.log(`Creating conductor layer: ${layerId}`);
          map.current.addLayer({
            id: layerId,
            type: 'line',
            source: 'conductors',
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
              visibility: 'visible'
            },
            paint: {
              'line-width': 3,
              ...style
            },
            filter: ['==', 'type', type]
          });
        } else {
          console.log(`Conductor layer already exists: ${layerId}`);
        }
      });
      
      // Now update the source data
      const features = conductors.map(conductor => {
        console.log('Creating feature for conductor:', conductor.type, 'with coordinates:', conductor.coordinates);
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: conductor.coordinates
          },
          properties: {
            type: conductor.type,
            wireSize: conductor.wireSize,
            length: conductor.length
          }
        };
      });
      
      console.log('Setting conductor source data with features:', features);
      map.current.getSource('conductors').setData({
        type: 'FeatureCollection',
        features: features
      });
      
      // Remove existing labels
      const existingLabels = document.getElementsByClassName('conductor-label-container');
      console.log('Removing', existingLabels.length, 'existing conductor labels');
      while (existingLabels[0]) {
        existingLabels[0].remove();
      }

      // Add labels for each conductor
      conductors.forEach((conductor, index) => {
        const coordinates = conductor.coordinates;
        if (coordinates && coordinates.length >= 2) {
          const start = coordinates[0];
          const end = coordinates[1];
          
          // Calculate midpoint for label placement
          const midpoint = [
            (start[0] + end[0]) / 2,
            (start[1] + end[1]) / 2
          ];

          // Create label container
          const labelContainer = document.createElement('div');
          labelContainer.className = 'conductor-label-container';

          // Create label element
          const label = document.createElement('div');
          label.className = 'conductor-label';
          const length = parseFloat(conductor.length);
          label.innerHTML = `${conductor.wireSize}<br>${isNaN(length) ? '0.0' : length.toFixed(1)} ft`;

          // Add label to container
          labelContainer.appendChild(label);

          // Add marker with label
          new mapboxgl.Marker({
            element: labelContainer,
            anchor: 'center',
            offset: [0, -2]
          })
            .setLngLat(midpoint)
            .addTo(map.current);

          // Calculate angle for label rotation
          const angle = Math.atan2(end[1] - start[1], end[0] - start[0]) * 180 / Math.PI;
          let textAngle = angle;
          if (angle > 90 || angle < -90) {
            textAngle += 180;
          }
          label.style.transform = `rotate(${textAngle}deg)`;
          
          console.log(`Added conductor label ${index + 1} at midpoint:`, midpoint);
        } else {
          console.warn(`Conductor ${index + 1} has invalid coordinates:`, coordinates);
        }
      });
      
      console.log('Conductor effect completed successfully');
    } else if (map.current && conductors.length === 0) {
      console.log('No conductors to display, clearing conductor source');
      // Clear conductor source if no conductors
      if (map.current.getSource('conductors')) {
        map.current.getSource('conductors').setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      
      // Remove existing labels
      const existingLabels = document.getElementsByClassName('conductor-label-container');
      while (existingLabels[0]) {
        existingLabels[0].remove();
      }
    } else {
      console.log('Map not ready or no conductors. Map exists:', !!map.current, 'Conductors count:', conductors.length);
    }
    console.log('=== END CONDUCTOR EFFECT DEBUG ===');
  }, [conductors]);

  // Load project data
  useEffect(() => {
    const loadProjectData = async () => {
      console.log('loadProjectData called for project ID:', id);
      if (id) {
        try {
          let projectData;
          
          // First try to load as a shared project
          try {
            projectData = await loadSharedProject(parseInt(id));
            console.log('Loaded as shared project');
          } catch (sharedError) {
            // If not a shared project, try loading as regular project
            console.log('Not a shared project, loading as regular project');
            projectData = await loadProject(parseInt(id));
          }
          
          if (projectData) {
            setProject(projectData);
            
            // Check if this is a shared project (read-only)
            if (projectData.isShared) {
              setIsReadOnly(true);
              console.log('Loading shared project in read-only mode');
            }
            
            // Restore equipment with proper structure
            const restoredEquipment = (projectData.equipment || []).map(item => ({
              ...item,
              lngLat: item.lngLat || item.coordinates, // Handle both formats
              details: item.details || item.properties || {},
              notes: item.notes || (item.properties?.notes || ''),
              photos: item.photos || []
            }));
            setEquipment(restoredEquipment);
            
            // Restore conductors
            console.log('=== CONDUCTOR LOADING DEBUG ===');
            console.log('Raw projectData.conductors:', projectData.conductors);
            console.log('projectData.conductors type:', typeof projectData.conductors);
            console.log('projectData.conductors length:', projectData.conductors?.length);
            
            const restoredConductors = (projectData.conductors || []).map((item, index) => {
              console.log(`Processing conductor ${index + 1}:`, item);
              console.log(`Conductor ${index + 1} coordinates:`, item.coordinates);
              console.log(`Conductor ${index + 1} coordinates type:`, typeof item.coordinates);
              console.log(`Conductor ${index + 1} coordinates length:`, item.coordinates?.length);
              
              const restored = {
                ...item,
                coordinates: item.coordinates || [],
                wireSize: item.wireSize || (item.properties?.wireSize || ''),
                length: item.length || (item.properties?.length || 0),
                notes: item.notes || (item.properties?.notes || ''),
                photos: item.photos || []
              };
              
              console.log(`Restored conductor ${index + 1}:`, restored);
              console.log(`Restored conductor ${index + 1} coordinates:`, restored.coordinates);
              
              return restored;
            });
            
            console.log('Final restoredConductors array:', restoredConductors);
            console.log('restoredConductors length:', restoredConductors.length);
            console.log('restoredConductors types:', restoredConductors.map(c => c.type));
            console.log('restoredConductors coordinates:', restoredConductors.map(c => c.coordinates));
            
            setConductors(restoredConductors);
            console.log('setConductors called with:', restoredConductors.length, 'conductors');
            console.log('=== END CONDUCTOR LOADING DEBUG ===');
            
            console.log('Restored conductors for shared project:', restoredConductors);
            console.log('Map current state:', map.current ? 'Map exists' : 'Map not ready');
            console.log('Conductor types found:', restoredConductors.map(c => c.type));
          }
        } catch (error) {
          console.error('Error loading project:', error);
        }
      }
    };

    loadProjectData();
  }, [id]);

  const handleEquipmentSelect = (e) => {
    const type = e.target.value;
    if (type) {
      setSelectedEquipment(type);
      setShowEquipmentSelect(false);
      setShowOtherSelect(false);
    }
  };

  const handleOtherSelect = (e) => {
    const type = e.target.value;
    if (type) {
      setSelectedOtherType(type);
      setShowEquipmentSelect(false);
      setShowOtherSelect(false);
    }
  };

  const handleDeleteEquipment = (index) => {
    const newEquipment = [...equipment];
    newEquipment.splice(index, 1);
    setEquipment(newEquipment);
  };

  const handleDeleteConductor = (index) => {
    const newConductors = [...conductors];
    newConductors.splice(index, 1);
    setConductors(newConductors);
  };

  const handleEditEquipment = (index) => {
    setEditingEquipment(equipment[index]);
    setSelectedEquipment(equipment[index].type);
    setClickedLngLat(equipment[index].lngLat);
    setIsComponentListOpen(false);
  };

  const handleEditConductor = (index) => {
    setEditingConductor(conductors[index]);
    setShowConductorForm(true);
  };

  const handleFormSubmit = (details) => {
    if (editingEquipment) {
      const newEquipment = equipment.map(item => 
        item === editingEquipment ? {
          ...item,
          details,
          notes: details.notes,
          photos: details.photos || item.photos || []
        } : item
      );
      setEquipment(newEquipment);
      setEditingEquipment(null);
    } else if (clickedLngLat && selectedEquipment) {
      setEquipment([...equipment, {
        type: selectedEquipment,
        lngLat: clickedLngLat,
        details,
        notes: details.notes,
        photos: details.photos || []
      }]);
    }
    setSelectedEquipment(null);
    setClickedLngLat(null);
  };

  const handleOtherFormSubmit = (details) => {
    if (clickedLngLat && selectedOtherType) {
      setEquipment([...equipment, {
        type: selectedOtherType,
        lngLat: clickedLngLat,
        details: {},
        notes: details.notes,
        photos: details.photos || []
      }]);
    }
    setSelectedOtherType(null);
    setClickedLngLat(null);
  };

  const handleConductorFormSubmit = (formData) => {
    if (editingConductor) {
      const newConductors = conductors.map(conductor =>
        conductor === editingConductor ? {
          ...conductor,
          type: formData.type,
          wireSize: formData.wireSize,
          length: parseFloat(formData.length) || 0,
          notes: formData.notes
        } : conductor
      );
      setConductors(newConductors);
      setEditingConductor(null);
    } else {
      setConductors([...conductors, {
        coordinates: conductorPoints,
        type: formData.type,
        wireSize: formData.wireSize,
        length: parseFloat(formData.length) || 0,
        notes: formData.notes
      }]);
    }
    setShowConductorForm(false);
    setConductorPoints([]);
    conductorPointsRef.current = [];
  };

  const toggleMapStyle = () => {
    setMapStyle(prevStyle => prevStyle === 'satellite' ? 'streets' : 'satellite');
  };

  const toggleConductorDrawing = () => {
    const newValue = !isDrawingConductor;
    setIsDrawingConductor(newValue);
    isDrawingConductorRef.current = newValue;
    setConductorPoints([]);
    conductorPointsRef.current = [];
  };

  // Calculate distance between two points in feet
  const calculateDistance = (point1, point2) => {
    const lat1 = point1[1];
    const lon1 = point1[0];
    const lat2 = point2[1];
    const lon2 = point2[0];
    
    const R = 20902231; // Earth radius in feet
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const mapCenter = map.current.getCenter();
      const viewport = {
        bounds: map.current.getBounds().toArray(),
        padding: { top: 50, bottom: 50, left: 50, right: 50 }
      };
      
      const projectData = {
        id: id ? parseInt(id) : undefined,
        name: project?.name || 'Untitled Project',
        customer: project?.customer || 'Unknown Customer',
        customerAddress: project?.customerAddress || '',
        customerPhone: project?.customerPhone || '',
        type: project?.type || 'residential',
        status: project?.status || 'in-progress',
        equipment: equipment.map((item, index) => ({
          id: item.id || index + 1,
          type: item.type,
          lngLat: item.lngLat,
          coordinates: item.lngLat, // Keep both for compatibility
          details: item.details || {},
          notes: item.notes || '',
          photos: item.photos || [],
          properties: {
            ...item.details,
            notes: item.notes,
            photos: item.photos || []
          }
        })),
        conductors: conductors.map((item, index) => ({
          id: item.id || index + 1,
          type: item.type,
          coordinates: item.coordinates,
          wireSize: item.wireSize,
          length: item.length,
          notes: item.notes || '',
          photos: item.photos || [],
          properties: {
            type: item.type,
            wireSize: item.wireSize,
            length: item.length,
            notes: item.notes,
            photos: item.photos || []
          }
        })),
        mapCenter: [mapCenter.lng, mapCenter.lat],
        mapZoom: map.current.getZoom(),
        mapStyle,
        viewport
      };

      const savedId = await saveProject(projectData);
      
      setIsSaving(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving project:', error);
      
      // Handle specific Firestore size limit errors
      if (error.message && error.message.includes('exceeds Firestore limit')) {
        alert('Some equipment or conductors have too much data (photos, notes, or details). The system will attempt to save without photos. Please reduce the amount of text or remove some photos and try again.');
      } else if (error.message && error.message.includes('too much data')) {
        alert('Some items have too much data. Please reduce notes or details and try again.');
      } else {
        alert('Error saving project. Please try again. If the problem persists, try reducing the number of photos or amount of text in your equipment details.');
      }
      
      setIsSaving(false);
    }
  };

  const showEquipmentCallout = (equipmentIndex) => {
    setVisibleCallouts(prev => new Set([...prev, equipmentIndex]));
  };

  const hideEquipmentCallout = (equipmentIndex) => {
    setVisibleCallouts(prev => {
      const newSet = new Set(prev);
      newSet.delete(equipmentIndex);
      return newSet;
    });
  };

  const hideAllCallouts = () => {
    setVisibleCallouts(new Set());
  };

  return (
    <div className="grid-map-pro">
      {/* Read-only indicator */}
      {isReadOnly && (
        <div className="read-only-banner">
          <div className="read-only-content">
            <span className="read-only-icon">👁️</span>
            <span className="read-only-text">Read-Only Mode - This project has been shared with you</span>
          </div>
        </div>
      )}
      
      <div ref={mapContainer} className="map-container">
        {drawingMessage && (
          <div className="drawing-message">{drawingMessage}</div>
        )}
        <div className="map-controls">
          <div className="map-controls-right">
            <button
              className="component-list-toggle"
              onClick={() => setIsComponentListOpen(true)}
            >
              Equipment List
            </button>
            {!isReadOnly && (
              <button
                className={`conductor-toggle ${isDrawingConductor ? 'active' : ''}`}
                onClick={toggleConductorDrawing}
              >
                {isDrawingConductor ? 'Cancel Drawing' : 'Draw Conductor'}
              </button>
            )}
            <button
              className="map-style-toggle"
              onClick={toggleMapStyle}
            >
              {mapStyle === 'streets' ? 'Satellite View' : 'Street View'}
            </button>
            {!isReadOnly && (
              <button
                className={`save-button ${isSaving ? 'saving' : ''}`}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Project'}
              </button>
            )}
          </div>
        </div>
        
        {/* Equipment Callouts */}
        {Array.from(visibleCallouts).map(equipmentIndex => {
          const equipmentItem = equipment[equipmentIndex];
          if (!equipmentItem) return null;
          
          return (
            <EquipmentCallout
              key={`callout-${equipmentIndex}`}
              equipment={equipmentItem}
              mapCenter={map.current ? [map.current.getCenter().lng, map.current.getCenter().lat] : [0, 0]}
              mapZoom={map.current ? map.current.getZoom() : 13}
              mapContainer={mapContainer}
              onClose={() => hideEquipmentCallout(equipmentIndex)}
            />
          );
        })}
      </div>
      
      <ComponentList
        isOpen={isComponentListOpen}
        onClose={() => setIsComponentListOpen(false)}
        equipment={equipment}
        conductors={conductors}
        onDeleteEquipment={isReadOnly ? null : handleDeleteEquipment}
        onDeleteConductor={isReadOnly ? null : handleDeleteConductor}
        onEditEquipment={isReadOnly ? null : handleEditEquipment}
        onEditConductor={isReadOnly ? null : handleEditConductor}
        isReadOnly={isReadOnly}
      />

      {!isReadOnly && showEquipmentSelect && showOtherSelect && (
        <div className="equipment-select-container">
          <div className="dropdowns-container">
            <div className="equipment-select-content">
              <h3>Select Equipment Type</h3>
              <select
                value=""
                onChange={handleEquipmentSelect}
                className="equipment-select"
              >
                <option value="">Choose equipment type...</option>
                {equipmentOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                className="cancel-button"
                onClick={() => {
                  setShowEquipmentSelect(false);
                  setShowOtherSelect(false);
                }}
              >
                Cancel
              </button>
            </div>
            
            <div className="equipment-select-content">
              <h3>Select Other Type</h3>
              <select
                value=""
                onChange={handleOtherSelect}
                className="equipment-select"
              >
                <option value="">Choose other type...</option>
                {otherOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                className="cancel-button"
                onClick={() => {
                  setShowEquipmentSelect(false);
                  setShowOtherSelect(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {!isReadOnly && selectedEquipment && (
        <EquipmentForm
          type={selectedEquipment}
          onSubmit={handleFormSubmit}
          onClose={() => setSelectedEquipment(null)}
        />
      )}

      {!isReadOnly && selectedOtherType && (
        <OtherForm
          type={selectedOtherType}
          onSubmit={handleOtherFormSubmit}
          onClose={() => setSelectedOtherType(null)}
        />
      )}

      {!isReadOnly && showConductorForm && conductorPoints.length === 2 && (
        <ConductorForm
          onSubmit={handleConductorFormSubmit}
          onClose={() => {
            setShowConductorForm(false);
            setIsDrawingConductor(false);
            setConductorPoints([]);
            conductorPointsRef.current = [];
            if (firstPointMarkerRef.current) {
              firstPointMarkerRef.current.remove();
              firstPointMarkerRef.current = null;
            }
          }}
          length={calculateDistance(conductorPoints[0], conductorPoints[1])}
        />
      )}
    </div>
  );
};

export default GridMapProApp;